
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, TrendingUp, Users, DollarSign } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Statistics = () => {
  const topWinners = [
    { rank: 1, name: 'Sarah M.', state: 'NSW', amount: 2500000, game: 'PowerBall' },
    { rank: 2, name: 'Michael K.', state: 'VIC', amount: 1800000, game: 'Oz Lotto' },
    { rank: 3, name: 'Emma T.', state: 'QLD', amount: 1200000, game: 'PowerBall' },
    { rank: 4, name: 'David L.', state: 'WA', amount: 950000, game: 'Saturday Lotto' },
    { rank: 5, name: 'Lisa R.', state: 'SA', amount: 750000, game: 'PowerBall' }
  ];

  const recentWins = [
    { name: 'John D.', amount: 25000, game: 'PowerBall', date: '2024-01-20' },
    { name: 'Amy S.', amount: 15000, game: 'Oz Lotto', date: '2024-01-19' },
    { name: 'Chris P.', amount: 8500, game: 'Saturday Lotto', date: '2024-01-18' },
    { name: 'Maria L.', amount: 12000, game: 'PowerBall', date: '2024-01-17' },
    { name: 'Robert W.', amount: 6750, game: 'Oz Lotto', date: '2024-01-16' }
  ];

  const gameStats = {
    powerball: {
      name: 'PowerBall',
      jackpot: 5000000,
      nextDraw: '2024-01-25',
      odds: '1 in 134,490,400',
      totalPrizes: 12500000
    },
    ozlotto: {
      name: 'Oz Lotto',
      jackpot: 8000000,
      nextDraw: '2024-01-23',
      odds: '1 in 45,379,620',
      totalPrizes: 18500000
    },
    saturday: {
      name: 'Saturday Lotto',
      jackpot: 3000000,
      nextDraw: '2024-01-22',
      odds: '1 in 8,145,060',
      totalPrizes: 8200000
    }
  };

  const monthlyStats = [
    { month: 'Jan 2024', totalPrizes: 45000000, winners: 2840, biggestWin: 2500000 },
    { month: 'Dec 2023', totalPrizes: 38000000, winners: 2156, biggestWin: 1800000 },
    { month: 'Nov 2023', totalPrizes: 42000000, winners: 2689, biggestWin: 3200000 },
    { month: 'Oct 2023', totalPrizes: 35000000, winners: 1978, biggestWin: 1500000 }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">GoldenTicketLucky Statistics</h1>
          <p className="text-xl text-gray-700">Discover the latest wins and lottery statistics</p>
        </div>
      </section>

      {/* Statistics Overview */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <Card>
              <CardContent className="p-6 text-center">
                <DollarSign className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-gray-900">$127M</h3>
                <p className="text-gray-600">Total Prizes This Year</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <Trophy className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-gray-900">8,456</h3>
                <p className="text-gray-600">Winners This Month</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <TrendingUp className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-gray-900">$2.5M</h3>
                <p className="text-gray-600">Biggest Win This Month</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <Users className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-gray-900">245K+</h3>
                <p className="text-gray-600">Active Players</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="winners" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="winners">Top Winners</TabsTrigger>
              <TabsTrigger value="recent">Recent Wins</TabsTrigger>
              <TabsTrigger value="games">Game Stats</TabsTrigger>
              <TabsTrigger value="monthly">Monthly Data</TabsTrigger>
            </TabsList>
            
            <TabsContent value="winners">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                    All-Time Top Winners
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topWinners.map((winner) => (
                      <div key={winner.rank} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                            winner.rank === 1 ? 'bg-yellow-500' :
                            winner.rank === 2 ? 'bg-gray-400' :
                            winner.rank === 3 ? 'bg-yellow-600' : 'bg-gray-300'
                          }`}>
                            {winner.rank}
                          </div>
                          <div>
                            <p className="font-medium">{winner.name}</p>
                            <p className="text-sm text-gray-600">{winner.state} • {winner.game}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">
                            ${winner.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="recent">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Winners (Last 7 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentWins.map((win, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{win.name}</p>
                          <p className="text-sm text-gray-600">{win.game} • {win.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            ${win.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="games">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.values(gameStats).map((game, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-yellow-600">{game.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Current Jackpot</p>
                        <p className="text-2xl font-bold text-green-600">
                          ${game.jackpot.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Next Draw</p>
                        <p className="font-medium">{game.nextDraw}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Odds of Winning</p>
                        <p className="font-medium">{game.odds}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Prizes</p>
                        <p className="font-medium">${game.totalPrizes.toLocaleString()}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="monthly">
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {monthlyStats.map((stat, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg">
                        <div>
                          <p className="text-sm text-gray-600">Month</p>
                          <p className="font-bold">{stat.month}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Prizes</p>
                          <p className="font-bold text-green-600">${stat.totalPrizes.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Winners</p>
                          <p className="font-bold">{stat.winners.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Biggest Win</p>
                          <p className="font-bold text-yellow-600">${stat.biggestWin.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Statistics;
